import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  alpha,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  Edit,
  HourglassEmpty,
  AccessTime,
  LocationOn,
  ContentCopy,
  Bed,
  Bathtub,
  Kitchen,
  Close,
  CheckCircle,
  Cancel,
  ArrowForward,
} from "@mui/icons-material";
import {
  Wifi as WifiIcon,
  Pets as PetsIcon,
  Pool as PoolIcon,
  LocalParking as ParkingIcon,
  Weekend as FurnishedIcon,
  FitnessCenter as GymIcon,
  Elevator as ElevatorIcon,
  FireExtinguisher as FireExtinguisherIcon,
  LocalHospital as FirstAidIcon,
  Alarm as AlarmIcon,
  Balcony as BalconyIcon,
} from "@mui/icons-material";


import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "react-toastify";
import cardPic from "../../../../assets/card_pic.webp";
import { getChargeAmounts } from "../../../../utils/chargeHelper";
import { BASE_URL } from "../../../../config/config";
import axios from "axios";
import { ValidateUser } from "../../../../redux/reducer/ValidateUser";
import { useSelector, useDispatch } from "react-redux";
import { GetProperty } from "../../../../redux/reducer/GetPropertyDetails";
import { useNavigate } from "react-router-dom";
import { useTheme as useAppTheme } from "../../../../context/ThemeContext";

const getOrdinal = (num) => {
  if (num === 0) return "Ground";
  if (num % 100 >= 11 && num % 100 <= 13) {
    return `${num}th`;
  }
  switch (num % 10) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

const CustomArrow = ({ onClick, direction }) => (
  <div
    className={`custom-arrow ${direction}`}
    onClick={onClick}
    role="button"
  />
);

const sliderSettings = {
  // dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  autoplay: true,
  autoplaySpeed: 4000,
  nextArrow: <CustomArrow direction="next" />,
  prevArrow: <CustomArrow direction="prev" />,
};

const PropertyCard = ({ flat, handleOpen, userId, validateUser, setFlats }) => {
  /* Card accents follow the owner's chosen theme */
  const { theme: appTheme } = useAppTheme();
  const accent = appTheme.primary;
  const [openRejectModal, setRejectOpenModal] = React.useState(false);
  const [showAllAmenities, setShowAllAmenities] = React.useState(false);
  const [togglingAvailability, setTogglingAvailability] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* One place decides what stage this property is at, so the chip on the photo
     and the strip under the card can never tell two different stories.
     `is_rent` comes back as 1 or "1" depending on the endpoint, hence Number(). */
  const rented = Number(flat.is_rent) === 1;
  // A vacated flat comes off the market automatically until the owner decides
  // to relist it (see vacateFlat.js on the server) — undefined/missing means
  // an older flat from before this field existed, which stays "on" by default.
  const isAvailable = flat.availabilityStatus !== false;
  const stage = rented
    ? "rented"
    : flat.is_rejected
    ? "rejected"
    : !flat.is_property_settings
    ? "needsSettings"
    : !flat.is_approved
    ? "inReview"
    : !isAvailable
    ? "paused"
    : "live";

  const STAGES = {
    needsSettings: {
      label: "Setup incomplete",
      chipBg: "#f59e0b",
      footerIcon: <InfoIcon sx={{ fontSize: 18 }} />,
      tone: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
      message:
        "Not visible to anyone yet. A property is sent to the admin only after its settings are applied.",
      action: "Apply settings",
    },
    inReview: {
      label: "In review",
      chipBg: "#0ea5e9",
      footerIcon: <AccessTime sx={{ fontSize: 18 }} />,
      tone: { bg: "#f0f9ff", border: "#bae6fd", text: "#075985" },
      message:
        "Settings applied and submitted. The admin approves within 24 hours — you'll get a notification, nothing to do until then.",
    },
    rejected: {
      label: "Rejected",
      chipBg: "#ef4444",
      footerIcon: <Cancel sx={{ fontSize: 18 }} />,
      tone: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" },
      message: "The admin sent this back. Read the reason, fix it and resubmit.",
      action: "See reason",
    },
    live: {
      label: "Live",
      chipBg: "#22c55e",
      footerIcon: <CheckCircle sx={{ fontSize: 18 }} />,
      tone: { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" },
      message: flat?.is_flat_interest_request
        ? "A tenant has requested this property. Details are locked while the request is open — handle it from Tenant New Request."
        : "Tenants can see and book it. Keep rent and photos accurate; any edit takes it back to the admin for approval and it stays hidden until then.",
    },
    rented: {
      label: "Rented",
      chipBg: "#3b82f6",
      footerIcon: <InfoIcon sx={{ fontSize: 18 }} />,
      tone: { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af" },
      message:
        "A tenant is living here. Property details and settings are locked and can only be changed once the flat is vacant.",
    },
    paused: {
      label: "Off market",
      chipBg: "#64748b",
      footerIcon: <InfoIcon sx={{ fontSize: 18 }} />,
      tone: { bg: "#f8fafc", border: "#cbd5e1", text: "#334155" },
      message:
        "Not visible to tenants right now — either just vacated and awaiting your decision, or paused by you. Turn it back on below when you want new tenants to find it.",
    },
  };

  const stageInfo = STAGES[stage];

  const handleStageAction = () => {
    if (stage === "needsSettings") {
      /* Carry the flat through so the settings form opens with it already
         ticked instead of making the owner find it. */
      navigate(`/properties-setting?flat=${flat._id}`);
    } else if (stage === "rejected") {
      setRejectOpenModal(true);
    }
  };

  // Amenities configuration
  const amenitiesConfig = [
    {
      key: "wifi",
      label: "WiFi",
      icon: <WifiIcon sx={{ fontSize: 16 }} />,
      value: String(flat.wifi).toLowerCase() === "true" || flat.wifi === 1,
    },
    {
      key: "pets",
      label: "Pets",
      icon: <PetsIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.petAllowed).toLowerCase() === "true" ||
        flat.petAllowed === 1,
    },
    {
      key: "pool",
      label: "Pool",
      icon: <PoolIcon sx={{ fontSize: 16 }} />,
      value: String(flat.pool).toLowerCase() === "true" || flat.pool === 1,
    },
    {
      key: "parking",
      label: "Parking",
      icon: <ParkingIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.parkingAvailable).toLowerCase() === "true" ||
        flat.parkingAvailable === 1,
    },
    {
      key: "furnished",
      label: "Furnished",
      icon: <FurnishedIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.furnished).toLowerCase() === "true" || flat.furnished === 1,
    },
    {
      key: "bathtub",
      label: "Bath Tub",
      icon: <Bathtub sx={{ fontSize: 16 }} />,
      value:
        String(flat.bathTub).toLowerCase() === "true" || flat.bathTub === 1,
    },
    {
      key: "balcony",
      label: "Balcony",
      icon: <BalconyIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.balcony).toLowerCase() === "true" || flat.balcony === 1,
    },
    {
      key: "elevator",
      label: "Elevator",
      icon: <ElevatorIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.elevator).toLowerCase() === "true" || flat.elevator === 1,
    },
    {
      key: "gym",
      label: "Gym",
      icon: <GymIcon sx={{ fontSize: 16 }} />,
      value: String(flat.gym).toLowerCase() === "true" || flat.gym === 1,
    },
    {
      key: "fireExtinguisher",
      label: "Fire Extinguisher",
      icon: <FireExtinguisherIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.fireExtinguisher).toLowerCase() === "true" ||
        flat.fireExtinguisher === 1,
    },
    {
      key: "firstAidKit",
      label: "First Aid",
      icon: <FirstAidIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.firstAidKit).toLowerCase() === "true" ||
        flat.firstAidKit === 1,
    },
    {
      key: "smokeAlarm",
      label: "Smoke Alarm",
      icon: <AlarmIcon sx={{ fontSize: 16 }} />,
      value:
        String(flat.smokeAlarm).toLowerCase() === "true" ||
        flat.smokeAlarm === 1,
    },
  ];

  const availableAmenities = amenitiesConfig.filter((a) => a.value);
  const visibleAmenities = showAllAmenities
    ? availableAmenities
    : availableAmenities.slice(0, 3);
  const remainingCount = availableAmenities.length - 3;

  const handleCopyLink = () => {
    console.log(flat?.tenantId == null, "flat");
    console.log(flat?.is_rent == 1, "flat");
    if (flat?.tenantId == null && flat?.is_rent == 1) {
      // for existing/old tenants which are not connected to owner yet
      navigator.clipboard.writeText(
        `${window.location.origin}/landlord/${userId}/property/${flat._id}?availability=all`
      );
    } else {
      navigator.clipboard.writeText(
        `${window.location.origin}/landlord/${userId}/property/${flat._id}`
      );
    }
    toast.dismiss();
    toast.success("Property link copied!");
  };

  // Only a fully-live (or paused) flat can be toggled — one still in review,
  // rejected, mid-setup, or actually rented has no listing state to flip.
  const canToggleAvailability =
    !rented &&
    flat.is_approved &&
    flat.is_property_settings &&
    !flat.is_rejected;

  const handleToggleAvailability = async (event) => {
    const nextAvailable = event.target.checked;
    setTogglingAvailability(true);
    try {
      const token = sessionStorage.getItem("ots_token");
      const response = await axios.patch(
        `${BASE_URL}/property-availability/${flat._id}`,
        { available: nextAvailable },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        const res = await dispatch(
          GetProperty({
            userId: validateUser?.UserValidate?.data?.user?._id,
            from: "landlord",
          })
        );
        if (res?.payload?.data?.success && res?.payload?.data?.data) {
          setFlats(res.payload.data.data);
        }
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update listing"
      );
    } finally {
      setTogglingAvailability(false);
    }
  };

  const handleRequestAgain = async () => {
    try {
      const token = sessionStorage.getItem("ots_token");
      const response = await axios.post(
        `${BASE_URL}/flat-request-again`,
        {
          requestId: flat?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // The server's message states that the property has gone back to the
        // admin and a decision email will arrive within 24 hours — the owner
        // gets that same message by email too.
        toast.success(
          response.data.message || "Request submitted again"
        );
        dispatch(ValidateUser({})); // Trigger refresh in parent component
        const res = await dispatch(
          GetProperty({
            userId: validateUser?.UserValidate?.data?.user?._id,
            from: "landlord",
          })
        );

        if (res?.payload?.data?.success && res?.payload?.data?.data) {
          setFlats(res.payload.data.data);
        } else {
          setFlats([]);
        }
        setRejectOpenModal(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit request");
    }
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          transition: "all 0.3s ease",
          bgcolor: "white",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 18px 34px -22px rgba(18,32,58,0.55)",
            borderColor: accent,
          },
        }}
      >
        {/* Image Slider */}
        <Box
          sx={{
            position: "relative",
            height: 220,
            bgcolor: "#f1f5f9",
          }}
        >
          {flat?.images?.length > 0 ? (
            <div className="flat-slider">
              <Slider {...sliderSettings}>
                {flat.images.map((img, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={img}
                    alt={`${flat.flatName} ${i + 1}`}
                    sx={{
                      width: "100%",
                      height: 220,
                      objectFit: "cover",
                    }}
                  />
                ))}
              </Slider>
            </div>
          ) : (
            <Box
              component="img"
              src={cardPic}
              alt="No image"
              sx={{
                width: "100%",
                height: 220,
                objectFit: "cover",
                filter: "brightness(0.9)",
              }}
            />
          )}

          {/* Status chip — same corner, same shape, every stage. The old
              full-width amber bar across the photo only appeared for two of
              the five states, which is what made a just-configured property
              look like a different kind of card. */}
          <Chip
            icon={
              stage === "live" || stage === "rented" ? (
                <CheckCircle sx={{ fontSize: 16 }} />
              ) : stage === "rejected" ? (
                <Cancel sx={{ fontSize: 16 }} />
              ) : (
                <HourglassEmpty sx={{ fontSize: 16 }} />
              )
            }
            label={stageInfo.label}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              bgcolor: stageInfo.chipBg,
              color: "white",
              fontWeight: 600,
              fontSize: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              "& .MuiChip-icon": {
                color: "white",
              },
            }}
          />

          {/* Action Buttons */}
          {flat.is_approved && (
            <Tooltip title="Copy Property Link" placement="left">
              <IconButton
                onClick={handleCopyLink}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  bgcolor: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": {
                    bgcolor: "#f8fafc",
                    transform: "scale(1.1)",
                  },
                }}
                size="small"
              >
                <ContentCopy sx={{ fontSize: 18, color: "#3b82f6" }} />
              </IconButton>
            </Tooltip>
          )}

          {flat.is_rejected && (
            <Tooltip title="View Rejection Reason" placement="left">
              <IconButton
                onClick={() => setRejectOpenModal(true)}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  bgcolor: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": {
                    bgcolor: "#fef2f2",
                    transform: "scale(1.1)",
                  },
                }}
                size="small"
              >
                <InfoIcon sx={{ fontSize: 18, color: "#ef4444" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <CardContent sx={{ p: 2 }}>
          {/* Header with Edit Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "#1e293b",
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {flat.flatName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 500,
                }}
              >
                {getOrdinal(flat.flatFloor)} Floor
              </Typography>
            </Box>
            <Tooltip
              title={
                rented
                  ? "A rented property is locked — nothing can be changed until it is vacant"
                  : flat?.is_flat_interest_request
                  ? "Property is under interest request, cannot be edited"
                  : "Edit Property"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleOpen(flat)}
                  disabled={rented || flat?.is_flat_interest_request}
                  sx={{
                    bgcolor: rented || flat?.is_flat_interest_request ? "#f1f5f9" : "#eef2ff",
                    color: rented || flat?.is_flat_interest_request ? "#94a3b8" : accent,
                    "&:hover": {
                      bgcolor: rented || flat?.is_flat_interest_request ? "#f1f5f9" : "#e0e7ff",
                    },
                  }}
                >
                  <Edit sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Rent & Advance */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#22c55e",
                }}
              >
                {(flat.flatRent || 0).toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                }}
              >
                PKR/month
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Advance: {(flat.flatAdvance || 0).toLocaleString()} PKR
            </Typography>
          </Box>

          {/* Location */}
          <Tooltip title={flat.mapAddress} arrow placement="top">
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                mb: 1.5,
                cursor: "pointer",
                "&:hover": {
                  "& .location-text": {
                    color: accent,
                  },
                },
              }}
            >
              <LocationOn
                sx={{
                  fontSize: 18,
                  color: accent,
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                className="location-text"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                  transition: "color 0.2s",
                }}
              >
                {flat.mapAddress}
              </Typography>
            </Box>
          </Tooltip>

          <Divider sx={{ my: 1.5 }} />

          {/* Room Details */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Bed sx={{ fontSize: 18, color: "#64748b" }} />
              <Typography variant="body2" fontWeight={600}>
                {flat.flatRooms}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Bathtub sx={{ fontSize: 18, color: "#64748b" }} />
              <Typography variant="body2" fontWeight={600}>
                {flat.flatToilet}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Kitchen sx={{ fontSize: 18, color: "#64748b" }} />
              <Typography variant="body2" fontWeight={600}>
                {flat.flatKitchen}
              </Typography>
            </Box>
          </Box>

          {/* Allowed For Chip */}
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label={
                flat.allowedFor === "family"
                  ? "Family Allowed"
                  : flat.allowedFor === "bachelor"
                  ? "Bachelors Allowed"
                  : "Both Allowed - Family & Bachelors"
              }
              size="small"
              sx={{
                bgcolor:
                  flat.allowedFor === "family"
                    ? alpha("#22c55e", 0.1)
                    : alpha("#ef4444", 0.1),
                color: flat.allowedFor === "family" ? "#22c55e" : "#ef4444",
                fontWeight: 600,
                fontSize: "12px",
              }}
            />
          </Box>

          {/* Amenities Section */}
          {availableAmenities.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1,
                  display: "block",
                }}
              >
                Amenities
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mb: 1,
                }}
              >
                {visibleAmenities.map((amenity) => (
                  <Tooltip key={amenity.key} title={amenity.label}>
                    <Chip
                      icon={amenity.icon}
                      label={amenity.label}
                      size="small"
                      sx={{
                        bgcolor: "#e0f2fe",
                        color: "#0369a1",
                        fontWeight: 500,
                        fontSize: "11px",
                        height: 26,
                        "& .MuiChip-icon": {
                          color: "#0369a1",
                          fontSize: 16,
                        },
                      }}
                    />
                  </Tooltip>
                ))}

                {remainingCount > 0 && (
                  <Chip
                    label={
                      showAllAmenities ? "Show Less" : `+${remainingCount} more`
                    }
                    size="small"
                    onClick={() => setShowAllAmenities(!showAllAmenities)}
                    sx={{
                      bgcolor: "#f1f5f9",
                      color: "#475569",
                      fontWeight: 600,
                      fontSize: "11px",
                      height: 26,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#e2e8f0",
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Maintenance Charges */}
          {getChargeAmounts(flat).maintenance > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "12px",
                mt: 1,
              }}
            >
              Maintenance: {getChargeAmounts(flat).maintenance} PKR
            </Typography>
          )}
        </CardContent>

        {/* Stage footer — what this property's state means, and the one thing
            the owner can do about it. Present on every card so the layout
            never jumps between states. */}
        <Box
          sx={{
            mt: "auto",
            bgcolor: stageInfo.tone.bg,
            borderTop: "1px solid",
            borderColor: stageInfo.tone.border,
            p: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box
            sx={{
              color: stageInfo.tone.text,
              mt: "1px",
              flexShrink: 0,
              display: "flex",
            }}
          >
            {stageInfo.footerIcon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "12px",
                color: stageInfo.tone.text,
                fontWeight: 500,
                lineHeight: 1.45,
              }}
            >
              {stageInfo.message}
            </Typography>
            {stageInfo.action && (
              <Button
                size="small"
                endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                onClick={handleStageAction}
                sx={{
                  mt: 0.75,
                  px: 0,
                  minWidth: 0,
                  fontSize: "12px",
                  fontWeight: 700,
                  color: stageInfo.tone.text,
                }}
              >
                {stageInfo.action}
              </Button>
            )}
            {canToggleAvailability && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  sx={{ mt: 0.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={isAvailable}
                      disabled={togglingAvailability}
                      onChange={handleToggleAvailability}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: 600, color: stageInfo.tone.text }}
                    >
                      {isAvailable ? "Available for rent" : "Listing paused"}
                    </Typography>
                  }
                />
                {togglingAvailability && <CircularProgress size={14} />}
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* Rejection Reason Dialog */}
      <Dialog
        open={openRejectModal}
        onClose={() => setRejectOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#fef2f2",
            color: "#dc2626",
            fontWeight: 700,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Cancel />
            Rejection Reason
          </Box>
          <IconButton
            size="small"
            onClick={() => setRejectOpenModal(false)}
            sx={{ color: "#dc2626" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            {flat.rejectionReason || "No reason provided by admin."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleRequestAgain}
            sx={{
              bgcolor: "#2563eb",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#1d4ed8",
              },
            }}
          >
            Request Again
          </Button>
          <Button
            onClick={() => setRejectOpenModal(false)}
            variant="contained"
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PropertyCard;
